import { useState } from 'react';
import type { ReactNode } from 'react';
import { CONCIERGE_SCOPE_LIST, SCOPES, scopeSpec } from '@yourstore/domain';
import { useAction, useApi } from './context';
import { Confirm, Empty, Failure, OnceBox, Section, When } from './bits';
import type { ApiKeyRow, KeyEnv, KeyIssued } from '../wire';

/**
 * The credentials, and the one screen in this console that handles a secret.
 *
 * Three rules, all of them the CLI's, restated because this is the second front
 * door onto the same table:
 *
 *  1. **The plaintext is shown once.** It is in the body of the response that
 *     created it and nowhere else — not in the key list (the column is omitted
 *     by projection), not in the audit row, not in a log line. Here it lives in
 *     component state until the box is dismissed, and dismissing **drops it**.
 *     There is no "show again", because a console that could show it again
 *     would be a console that kept it.
 *  2. **Two active per (tenant, integration).** That is a database constraint,
 *     not a convention. The console does not pre-empt it; when it fires, the
 *     server's sentence names the rotation order — issue new, switch, revoke
 *     old — and that sentence is what the operator reads.
 *  3. **A scope for a module the tenant has not bought is not grantable.** The
 *     boxes for such scopes are disabled with the module named, because the
 *     effective grant is `key.scopes ∩ scopes(tenant.modules)` and a key whose
 *     written permissions differ from its real ones is a support ticket waiting
 *     to happen. The server still refuses independently; this only stops the
 *     click that was going to be refused.
 *
 * Revocation is confirmed and its honesty is preserved: the response says
 * `effective_within_seconds: 15`, because the key directory is cached in memory
 * for 15 seconds (spec §5.2). Rounding that to "revoked" is how an operator
 * concludes it did not work and does it twice.
 */
export function TenantKeys({
  tenantId,
  keys,
  enabledModules,
  onChanged,
}: {
  tenantId: string;
  keys: ApiKeyRow[];
  enabledModules: string[];
  onChanged: () => void;
}): ReactNode {
  const api = useApi();
  const issue = useAction();
  const revoke = useAction();

  const [showRevoked, setShowRevoked] = useState(false);
  const [issuing, setIssuing] = useState(false);
  const [integration, setIntegration] = useState('aiconcierge');
  const [env, setEnv] = useState<KeyEnv>('test');
  const [scopes, setScopes] = useState<string[]>([...CONCIERGE_SCOPE_LIST]);
  const [issued, setIssued] = useState<KeyIssued | null>(null);
  const [revoking, setRevoking] = useState<ApiKeyRow | null>(null);

  const enabled = new Set(enabledModules);
  const shown = showRevoked ? keys : keys.filter((k) => k.status === 'active');

  const submit = async (): Promise<void> => {
    const ok = await issue.run(async () => {
      const answer = await api.issueKey(tenantId, { integration, env, scopes });
      setIssued(answer);
    });
    if (ok) {
      setIssuing(false);
      onChanged();
    }
  };

  const confirmRevoke = async (): Promise<void> => {
    if (revoking === null) return;
    const ok = await revoke.run(async () => {
      await api.revokeKey(tenantId, revoking.key_id);
    });
    if (ok) {
      setRevoking(null);
      onChanged();
    }
  };

  return (
    <Section
      title="API keys"
      note="Stored as a SHA-256 hash only. Two active per (tenant, integration); revocation is immediate in the database and takes effect on every warm instance within 15 seconds (spec §5.2)."
      actions={
        <div className="row-actions">
          <label className="inline-field">
            <input
              type="checkbox"
              checked={showRevoked}
              onChange={(e) => setShowRevoked(e.target.checked)}
            />
            <span>Show revoked</span>
          </label>
          <button
            type="button"
            className="button button--primary"
            onClick={() => {
              issue.clear();
              setIssuing((v) => !v);
            }}
          >
            New key
          </button>
        </div>
      }
    >
      {/*
        The plaintext, above everything, and dismissible only by the operator
        saying they have stored it. It is deliberately *not* inside the issue
        form: closing the form must not be able to take the key with it.
      */}
      {issued !== null ? (
        <>
          <OnceBox
            label={`${issued.prefix}… — ${issued.integration} (${issued.env})`}
            value={issued.plaintext}
            note={issued.plaintext_note}
            onDismiss={() => setIssued(null)}
          />
          {issued.warnings.map((w) => (
            <p key={w} className="warn">
              {w}
            </p>
          ))}
        </>
      ) : null}

      {issuing ? (
        <form
          className="form"
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <div className="form__row">
            <label className="field">
              <span className="field__label">Integration</span>
              <input
                value={integration}
                onChange={(e) => setIntegration(e.target.value)}
                placeholder="aiconcierge"
              />
              <span className="field__hint">
                Two active keys per integration — that is the rotation limit.
              </span>
            </label>
            <label className="field">
              <span className="field__label">Environment</span>
              <select value={env} onChange={(e) => setEnv(e.target.value as KeyEnv)}>
                <option value="test">test</option>
                <option value="live">live</option>
              </select>
              <span className="field__hint">
                The credential names its environment: <code>ys_test_</code> / <code>ys_live_</code>.
              </span>
            </label>
          </div>

          <fieldset className="scopes">
            <legend>Scopes — nothing is granted by default (spec §3.3)</legend>
            {SCOPES.map((s) => {
              const grantable = enabled.has(s.module);
              return (
                <label key={s.scope} className={`scope${grantable ? '' : ' is-blocked'}`}>
                  <input
                    type="checkbox"
                    disabled={!grantable}
                    checked={scopes.includes(s.scope)}
                    onChange={(e) =>
                      setScopes((list) =>
                        e.target.checked ? [...list, s.scope] : list.filter((x) => x !== s.scope),
                      )
                    }
                  />
                  <code>{s.scope}</code>
                  <span className="muted"> {s.grants}</span>
                  {grantable ? null : (
                    <span className="flag flag--bad">{s.module} is not enabled</span>
                  )}
                </label>
              );
            })}
          </fieldset>

          {issue.failure !== null ? <Failure failure={issue.failure} /> : null}

          <div className="row-actions">
            <button type="button" className="button" onClick={() => setIssuing(false)}>
              Cancel
            </button>
            <button
              type="submit"
              className="button button--primary"
              disabled={issue.busy || scopes.length === 0}
            >
              {issue.busy ? 'Issuing…' : 'Issue key'}
            </button>
          </div>
        </form>
      ) : null}

      {!issuing && issue.failure !== null ? <Failure failure={issue.failure} /> : null}
      {revoking === null && revoke.failure !== null ? <Failure failure={revoke.failure} /> : null}

      {shown.length === 0 ? (
        <Empty>No active keys. Until one is issued, the partner cannot authenticate at all.</Empty>
      ) : (
        <table className="table table--dense">
          <thead>
            <tr>
              <th>Prefix</th>
              <th>Integration</th>
              <th>Env</th>
              <th>Slot</th>
              <th>Scopes</th>
              <th>Last used</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {shown.map((k) => (
              <tr key={k.key_id} className={k.status === 'revoked' ? 'is-muted' : undefined}>
                <td>
                  <code>{k.prefix}…</code>
                </td>
                <td>{k.integration}</td>
                <td>{k.env}</td>
                <td>{k.active_slot ?? '—'}</td>
                <td className="muted">
                  {k.scopes.map((s) => scopeSpec(s)?.scope ?? s).join(', ')}
                </td>
                <td>
                  {k.last_used_at === null ? (
                    <span className="muted">never</span>
                  ) : (
                    <When iso={k.last_used_at} />
                  )}
                </td>
                <td>{k.status}</td>
                <td>
                  {k.status === 'active' ? (
                    <button
                      type="button"
                      className="button button--danger"
                      onClick={() => {
                        revoke.clear();
                        setRevoking(k);
                      }}
                    >
                      Revoke
                    </button>
                  ) : (
                    <span className="muted">
                      <When iso={k.revoked_at} />
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {revoking !== null ? (
        <Confirm
          title={`Revoke ${revoking.prefix}… on ${tenantId}?`}
          consequence={
            `Whoever holds this key stops authenticating. It is immediate in the database and ` +
            `takes effect on every warm instance within 15 seconds — the key directory is cached ` +
            `in memory (spec §5.2). A revoked key cannot be restored; issue another.`
          }
          confirmLabel="Revoke key"
          busy={revoke.busy}
          failure={revoke.failure}
          onConfirm={() => void confirmRevoke()}
          onCancel={() => {
            revoke.clear();
            setRevoking(null);
          }}
        />
      ) : null}
    </Section>
  );
}
